// Записът на листа „Доставка": Ctrl+Enter / „Запиши доставката" минава през
// пречките (нова доставка без доставчик, празен ред, липсващ склад…) → проблемните продукти („Задръж" или
// „Запиши без тях") → модала „Запис на доставката" (дата, номер, плащане).
// При „без тях" проблемните остават като задържана чернова. Пречка по реда
// (`deliveryIssues.ts`) → съобщение с продуктите, курсорът в първата проблемна
// клетка на екрана и от този момент — червена рамка на празните полета.
import { useState, type MutableRefObject } from "react";
import { App } from "antd";
import { useTranslation } from "react-i18next";
import { useCreateDelivery, useUpdateDelivery } from "../../../../hooks/useInventory";
import type { Delivery, DeliveryDocumentType } from "../../../../api/inventory";
import type { DeliverySaveValues } from "./DeliverySaveModal";
import { buildDeliveryPayload, deliveryErrorText } from "./deliveryWorksheetHelpers";
import { invalidCellKeys, type DeliveryIssue, type DeliveryIssueField } from "./deliveryIssues";
import { parseDecimal, type DeliveryRowDraft } from "./deliveryWorksheetShared";

type DraftSync = {
  hold: (problemText: string) => Promise<void>;
  remove: () => Promise<void>;
};

type Params = {
  mode: "new" | "edit";
  deliveryId: string | undefined;
  header: {
    warehouseId: number | null;
    supplierId: number | null;
    note: string;
    documentType: DeliveryDocumentType;
    linkedNoteIds: number[];
  };
  hasLinkedNotes: boolean;
  // нова доставка без доставчик не се записва — фокусът отива в полето
  supplierRequired: boolean;
  onMissingSupplier: () => void;
  rows: {
    validEntries: [string, DeliveryRowDraft][];
    addedIds: number[];
    rowOf: (productId: number) => DeliveryRowDraft;
    qtyBase: (row: DeliveryRowDraft) => number;
    computeIssues: () => DeliveryIssue[];
    replaceAllRows: (rows: Record<number, DeliveryRowDraft>, order: number[]) => void;
  };
  nameOf: (productId: number) => string;
  draftSync: DraftSync;
  dirtyRef: MutableRefObject<boolean>;
  focusCell: (productId: number, col: number) => void;
  onSavedHeader: (values: DeliverySaveValues) => void;
  onDone: (saved: Delivery | null) => void;
};

export const useDeliverySave = ({
  mode,
  deliveryId,
  header,
  hasLinkedNotes,
  supplierRequired,
  onMissingSupplier,
  rows,
  nameOf,
  draftSync,
  dirtyRef,
  focusCell,
  onSavedHeader,
  onDone,
}: Params) => {
  const { t } = useTranslation();
  const { message } = App.useApp();
  const createMutation = useCreateDelivery();
  const updateMutation = useUpdateDelivery();
  const [saveOpen, setSaveOpen] = useState(false);
  const [probSaveOpen, setProbSaveOpen] = useState(false);
  const [excludeProblems, setExcludeProblems] = useState(false);
  // Червената рамка на количество/цена/склад — чак след първия опит за запис
  // (иначе празната цена светва още докато се пише количеството); партида и
  // срок са задължителни по продукт и светят веднага, както досега.
  const [issuesRevealed, setIssuesRevealed] = useState(false);

  const pending = createMutation.isPending || updateMutation.isPending;
  const problemRows = rows.validEntries.filter(([, row]) => !!row.problem);
  // Редовете, които записът ще заприходи (без проблемните при „Запиши без тях")
  const savedEntries = excludeProblems ? rows.validEntries.filter(([, row]) => !row.problem) : rows.validEntries;
  // За подсказката „продадено без наличност" в модала за запис: продукт ·
  // склад · количество в базовата мярка; при редакция — без самата доставка
  const incomingLines = savedEntries.map(([pidStr, row]) => ({
    productId: +pidStr,
    warehouseId: row.warehouseId as number,
    quantity: rows.qtyBase(row),
  }));
  const editMovementId = mode === "edit" && deliveryId ? Number(deliveryId) : null;
  // Празната фактура е валидна, ако обобщава стокови. Липсващият доставчик е
  // първата пречка — той решава цените и стоковите, които фактурата обобщава.
  const missingSupplier = supplierRequired && header.supplierId == null;
  const issues: DeliveryIssue[] = [
    ...(missingSupplier
      ? [{ kind: "header" as const, text: t("inventoryPage.issueSupplier"), productIds: [] }]
      : []),
    ...rows.computeIssues().filter((issue) => !(hasLinkedNotes && issue.kind === "empty")),
  ];
  const invalidCells = invalidCellKeys(issues);
  const isCellInvalid = (productId: number, field: DeliveryIssueField) =>
    (issuesRevealed || field === "batch" || field === "expiry") && invalidCells.has(`${productId}:${field}`);
  // „Кафе: Повредена · … · Мляко: Липсва количество" — за задържаната чернова
  const problemText = () => problemRows.map(([pidStr, row]) => `${nameOf(+pidStr)}: ${row.problem}`).join(" · ");

  const trySave = () => {
    if (pending) return;
    if (issues.length) {
      message.error(issues.map((issue) => issue.text).join(" · "));
      setIssuesRevealed(true);
      // Доставчикът е първата пречка; иначе — първата проблемна клетка на екрана
      const focus = issues.find((issue) => issue.focus)?.focus;
      if (missingSupplier) onMissingSupplier();
      else if (focus) focusCell(focus.productId, focus.col);
      return;
    }
    if (problemRows.length && !excludeProblems) {
      setProbSaveOpen(true);
      return;
    }
    setSaveOpen(true);
  };

  const submit = (values: DeliverySaveValues) => {
    if (header.warehouseId == null) return;
    const payload = buildDeliveryPayload({
      entries: savedEntries,
      qtyBase: rows.qtyBase,
      values,
      warehouseId: header.warehouseId,
      supplierId: header.supplierId,
      note: header.note,
      documentType: header.documentType,
      linkedNoteIds: header.linkedNoteIds,
      withPayment: mode === "new",
    });
    const onSuccess = (saved: Delivery) => {
      dirtyRef.current = false; // запазено → напускането не пита
      setSaveOpen(false);
      if (mode === "new" && problemRows.length && excludeProblems) {
        // проблемните остават като задържана чернова
        rows.replaceAllRows(Object.fromEntries(problemRows), problemRows.map(([pidStr]) => +pidStr));
        void draftSync.hold(problemText()).then(() => onDone(saved));
        return;
      }
      if (mode === "new") void draftSync.remove();
      onDone(saved);
    };
    const onError = (e: unknown) => {
      console.error("Delivery save error:", e);
      message.error(deliveryErrorText(e, t));
      // Грешка по реда → фокус в първото празно Количество
      const firstBad = rows.addedIds.find((id) => parseDecimal(rows.rowOf(id).qty) <= 0);
      if (firstBad != null) focusCell(firstBad, 1);
    };
    onSavedHeader(values);
    if (mode === "edit") updateMutation.mutate({ id: Number(deliveryId), payload }, { onSuccess, onError });
    else createMutation.mutate(payload, { onSuccess, onError });
  };

  // „Задръж доставката" — целият лист остава чернова със статус „задържана"
  const hold = () => {
    setProbSaveOpen(false);
    if (mode !== "new") return;
    void draftSync.hold(problemText()).then(() => {
      dirtyRef.current = false;
      message.info(t("inventoryPage.heldToast"));
      onDone(null);
    });
  };

  const saveWithoutProblems = () => {
    setProbSaveOpen(false);
    setExcludeProblems(true);
    setSaveOpen(true);
  };

  return {
    pending,
    issues,
    isCellInvalid,
    problemRows,
    excludeProblems,
    incomingLines,
    editMovementId,
    saveOpen,
    closeSave: () => setSaveOpen(false),
    probSaveOpen,
    closeProbSave: () => setProbSaveOpen(false),
    trySave,
    submit,
    hold,
    saveWithoutProblems,
  };
};
